import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ResearchGrantMatch } from '../../models/research.models';
import { DaysUntilClose, FormatDayFirstDate, GrantClosing } from '../shared/grant-dates';
import { ShortEligibilityLabels } from './eligibility-labels';

/** The mockup switches the Closes value to its urgent red treatment inside a fortnight (task-5 brief, Step 3). */
const CLOSING_SOON_DAYS = 14;
/** `geoLine()` in grants.html: 32 characters plus an ellipsis once the value runs past 34. */
const LOCATION_MAX_CHARS = 34;

/** "$453K" / "$1.2M" / "$800" — compact USD, no cents (grant amounts on stage are always whole dollars). */
function FormatUSDShort(amount: number): string {
    if (amount >= 1_000_000) {
        const millions = amount / 1_000_000;
        return `$${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
    }
    if (amount >= 1_000) {
        return `$${Math.round(amount / 1_000)}K`;
    }
    return `$${amount}`;
}

/**
 * "Up to $453K" (ceiling only) / "$300K – $500K" (floor and ceiling both
 * stated and differ) / "Not specified". Pure so `GrantDetailComponent` can
 * reuse it for its own Award fact tile rather than re-deriving the same three
 * cases.
 *
 * The empty case reads `Not specified` because that is the mockup's own
 * wording (grants.html `amountLine()`, parity/grants.md §6). The range keeps
 * the spaced en dash rather than the mockup's comma — parity/grants.md §6
 * flags that comma as a mockup defect (it renders "$1, $453K").
 */
export function FormatAwardLine(grant: Pick<ResearchGrantMatch, 'awardFloorUSD' | 'awardCeilingUSD'>): string {
    const { awardFloorUSD, awardCeilingUSD } = grant;
    if (awardFloorUSD !== null && awardCeilingUSD !== null && awardFloorUSD !== awardCeilingUSD) {
        return `${FormatUSDShort(awardFloorUSD)} – ${FormatUSDShort(awardCeilingUSD)}`;
    }
    if (awardCeilingUSD !== null) return `Up to ${FormatUSDShort(awardCeilingUSD)}`;
    if (awardFloorUSD !== null) return `From ${FormatUSDShort(awardFloorUSD)}`;
    return 'Not specified';
}

/**
 * "11 Sep 2026" — the mockup's date shape (`fmtDate`, grants.html), or the
 * honest fallback when there is no dated deadline.
 */
export function FormatClosingDate(grant: GrantClosing): string {
    if (grant.closingIsRolling) return 'Rolling';
    if (!grant.closingDate) return 'Not stated';
    return FormatDayFirstDate(grant.closingDate);
}

/**
 * The tail after the date: "in 23 days" / "9 days left" (inside a fortnight,
 * rendered in the portal's urgent orange) / "Closed" / "today", and null when
 * the date itself is the whole value. Separate from `FormatClosingDate` so the
 * card can colour only the tail, exactly as the mockup's `.soon` / `.closed`
 * spans do. `now` is injectable for tests; defaults to the real clock.
 */
export function FormatClosingSuffix(grant: GrantClosing, now: Date = new Date()): string | null {
    const days = DaysUntilClose(grant, now);
    if (days === null) return null;
    if (days < 0) return 'Closed';
    if (days === 0) return 'today';
    if (days <= CLOSING_SOON_DAYS) return `${days} day${days === 1 ? '' : 's'} left`;
    return `in ${days} days`;
}

/** True while a dated deadline is open but a fortnight away or less — the mockup's `.soon` treatment. */
export function IsClosingSoon(grant: GrantClosing, now: Date = new Date()): boolean {
    const days = DaysUntilClose(grant, now);
    return days !== null && days >= 0 && days <= CLOSING_SOON_DAYS;
}

/**
 * "11 Sep 2026 · in 23 days" / "28 Aug 2026 · 9 days left" / "01 Aug 2026 ·
 * Closed" / "Rolling" / "Not stated" — the date and its tail as one string,
 * for the surfaces that cannot colour part of a line (`GrantDetailComponent`,
 * `DescribeGrantMeta`).
 */
export function FormatClosingLine(grant: GrantClosing, now: Date = new Date()): string {
    const date = FormatClosingDate(grant);
    const suffix = FormatClosingSuffix(grant, now);
    return suffix ? `${date} · ${suffix}` : date;
}

/** First three eligible-applicant values, " · "-joined, "+N" for the rest; the mockup's "See announcement" when empty (`eligLine()`, grants.html:744). */
export function FormatEligibleLine(eligibility: readonly string[]): string {
    if (eligibility.length === 0) return 'See announcement';
    if (eligibility.length <= 3) return eligibility.join(' · ');
    return `${eligibility.slice(0, 3).join(' · ')} +${eligibility.length - 3}`;
}

/**
 * The mockup's conditional fourth fact row: the grant's geographic
 * restriction, truncated the way `geoLine()` truncates it, or null when the
 * grant carries none (the row is then omitted rather than printed empty).
 */
export function FormatGrantLocationLine(regions: readonly string[]): string | null {
    const value = regions.filter((r) => Boolean(r && r.trim())).join(', ');
    if (!value) return null;
    return value.length > LOCATION_MAX_CHARS ? `${value.slice(0, LOCATION_MAX_CHARS - 2)}…` : value;
}

/** The funder-type chip's palette: teal for federal, plum for private, neutral for everything else (`.t-Federal`/`.t-Private`/`.t-State`, parity/grants.md §4.5). */
export type GrantFunderChipTone = 'teal' | 'plum' | 'neutral';

export function GrantFunderTone(funderType: string | null): GrantFunderChipTone {
    const value = (funderType ?? '').trim().toLowerCase();
    if (value === 'federal') return 'teal';
    if (value === 'private') return 'plum';
    return 'neutral';
}

/**
 * A grant result card: wraps `agu-item-card` with the grant-specific chip
 * (funder type), title, funder line, the Award/Closes/Eligible/Location facts
 * and the two-CTA row (View details, Apply now ↗). `Save`/`Pin`/`Dismiss`/
 * `Open` relay the underlying item-card events under grant-domain names so the
 * page component's handlers read as grant actions, not generic card plumbing.
 */
@Component({
    standalone: false,
    selector: 'ra-grant-card',
    templateUrl: './grant-card.component.html',
    styleUrl: './grant-card.component.scss',
})
export class GrantCardComponent implements OnChanges {
    @Input({ required: true }) Grant!: ResearchGrantMatch;
    @Input() Recommended = false;
    /** Every card carries the pin in the mockup, on both tabs (grants.html:768-772); the Saved page opts out. */
    /** The title's heading level: 2 on the Saved board (P/for-you.html:947), 3 in the finders. */
    @Input() TitleLevel: 2 | 3 = 3;
    @Input() ShowPin = true;
    @Input() ShowDismiss = true;
    /**
     * The Saved board's deadline chip ("Due 12 Sep · 23d"), which the mockup
     * renders IN the saved card's chip row beside the Saved badge
     * (`.duetag`, saved.html:263 and 396). Null on the finders, which state the
     * same deadline in their Closes fact instead — this card never derives it,
     * because the board already computes every row's deadline once at load time
     * rather than per change-detection pass (SavedPageComponent's own note).
     */
    @Input() DueChip: string | null = null;

    @Output() Save = new EventEmitter<boolean>();
    @Output() Pin = new EventEmitter<boolean>();
    @Output() Dismiss = new EventEmitter<void>();
    @Output() Open = new EventEmitter<void>();

    /**
     * Computed once per `Grant` change, not template-bound getters — a getter
     * re-reads `Date.now()` (via the `now = new Date()` defaults below) on
     * every change-detection pass, which is exactly the "never bind
     * time-dependent calls in templates" trap (ui-best-practices §7:
     * NG0100-adjacent — two dev-mode CD passes disagreeing on the value).
     */
    ClosingDate = 'Not stated';
    ClosingSuffix: string | null = null;
    ClosingSoon = false;
    /**
     * Both closed treatments — the greyed `Closes` tail AND the card's own 66%
     * dimming — follow the DATE, which is what the mockup's own test is
     * (`var closed = g.days<0`, grants.html:753). `Grant.isExpired` is not that
     * signal: `IsGrantExpired` (apps/MJAPI/src/resolvers/research/grantSearch.ts)
     * is `source === 'Grants Expired' OR closingDate < todayUtc`, so a
     * source-flagged row with a future deadline would dim the card while its own
     * Closes fact still read `in 40 days`.
     */
    ClosingIsClosed = false;

    ngOnChanges(): void {
        this.ClosingDate = FormatClosingDate(this.Grant);
        this.ClosingSuffix = FormatClosingSuffix(this.Grant);
        this.ClosingSoon = IsClosingSoon(this.Grant);
        this.ClosingIsClosed = this.ClosingSuffix === 'Closed';
    }

    get AwardLine(): string {
        return FormatAwardLine(this.Grant);
    }

    get EligibleLine(): string {
        return FormatEligibleLine(ShortEligibilityLabels(this.Grant.eligibility));
    }

    get LocationLine(): string | null {
        return FormatGrantLocationLine(this.Grant.regions);
    }

    get FunderTone(): GrantFunderChipTone {
        return GrantFunderTone(this.Grant.funderType);
    }
}
